import { useRef, useState } from "react";
import { ImageOff, Play } from "lucide-react";
import { isVideoFile, mediaIdFromUrl, mediaSrc, parseVideoEntry, vimeoId, youtubeId } from "../lib/media";

export function SafeImage({ src, alt = "", className = "", fallbackClassName = "", hideOnError = false, onLoadError, ...props }) {
  const [failed, setFailed] = useState(false);
  const resolved = mediaSrc(src);
  if (!resolved || failed) {
    if (hideOnError) return null;
    return (
      <div className={`flex min-h-28 items-center justify-center bg-[#11100F] text-white/35 ${fallbackClassName || className}`} aria-label={alt || "Image unavailable"}>
        <ImageOff className="h-6 w-6" aria-hidden="true" />
      </div>
    );
  }
  return <img {...props} src={resolved} alt={alt} className={className} onError={(e) => { setFailed(true); onLoadError?.(e); }} />;
}

function PlayBadge() {
  return (
    <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/75 bg-[#290B11]/75 text-white shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-105">
        <Play className="ml-1 h-6 w-6 fill-current" />
      </span>
    </span>
  );
}

export function PortfolioVideo({ entry, title = "Video" }) {
  const { url, thumbnail } = parseVideoEntry(entry);
  const resolved = mediaSrc(url);
  const mediaId = mediaIdFromUrl(url);
  const yt = youtubeId(url);
  const vm = vimeoId(url);
  const playableSrc = mediaId ? mediaSrc(`/api/media/${mediaId}/playable`) : resolved;
  const autoPoster = mediaId ? mediaSrc(`/api/media/${mediaId}/poster`) : "";
  const poster = mediaSrc(thumbnail || (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : autoPoster));
  const [playing, setPlaying] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const videoRef = useRef(null);

  const startPreview = () => {
    const v = videoRef.current;
    if (!v || playing) return;
    try {
      v.muted = true;
      v.playbackRate = 1;
      const promise = v.play();
      if (promise?.catch) promise.catch(() => {});
    } catch {}
  };

  const stopPreview = () => {
    const v = videoRef.current;
    if (!v || playing) return;
    try {
      v.pause();
      if (Number.isFinite(v.duration) && v.duration > 0) v.currentTime = Math.min(0.2, v.duration / 12);
    } catch {}
  };

  const DirectPreview = () => (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onFocus={startPreview}
      onBlur={stopPreview}
      className="portfolio-video-preview group"
      aria-label={`${title} — play video`}
    >
      {!previewFailed && playableSrc ? (
        <video
          ref={videoRef}
          src={playableSrc}
          poster={poster || undefined}
          muted
          playsInline
          preload="metadata"
          className="portfolio-video-live-preview h-full w-full object-cover"
          onError={() => setPreviewFailed(true)}
          onLoadedMetadata={(e) => {
            try {
              const v = e.currentTarget;
              if (Number.isFinite(v.duration) && v.duration > 0) v.currentTime = Math.min(0.2, v.duration / 12);
            } catch {}
          }}
        />
      ) : poster ? (
        <SafeImage src={poster} alt={title} className="portfolio-video-poster h-full w-full object-cover" hideOnError />
      ) : (
        <div className="portfolio-video-fallback" aria-hidden="true">
          <span className="portfolio-video-fallback-mark">YUXEL</span>
        </div>
      )}
      <span className="portfolio-media-overlay" />
      <PlayBadge />
      <span className="portfolio-media-caption">{title}</span>
    </button>
  );

  if (isVideoFile(url) || mediaId || (!yt && !vm && resolved)) {
    if (playing) {
      return (
        <div className="portfolio-video-player">
          <video src={playableSrc} poster={poster || undefined} controls autoPlay playsInline className="h-full w-full object-contain" />
        </div>
      );
    }
    return <DirectPreview />;
  }

  if (yt) {
    if (playing) {
      return (
        <div className="portfolio-video-player">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${yt}?rel=0&autoplay=1`}
            title={title}
            className="h-full w-full"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      );
    }
    return (
      <button type="button" onClick={() => setPlaying(true)} className="portfolio-video-preview group" aria-label={`${title} — play video`}>
        <SafeImage src={poster} alt={title} className="portfolio-video-poster h-full w-full object-cover" hideOnError />
        <span className="portfolio-media-overlay" />
        <PlayBadge />
        <span className="portfolio-media-caption">{title}</span>
      </button>
    );
  }

  if (vm) {
    if (playing) {
      return (
        <div className="portfolio-video-player">
          <iframe
            src={`https://player.vimeo.com/video/${vm}?title=0&byline=0&portrait=0&dnt=1&autoplay=1`}
            title={title}
            className="h-full w-full"
            loading="lazy"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    if (poster) {
      return (
        <button type="button" onClick={() => setPlaying(true)} className="portfolio-video-preview group" aria-label={`${title} — play video`}>
          <SafeImage src={poster} alt={title} className="portfolio-video-poster h-full w-full object-cover" hideOnError />
          <span className="portfolio-media-overlay" />
          <PlayBadge />
          <span className="portfolio-media-caption">{title}</span>
        </button>
      );
    }
    return (
      <button type="button" onClick={() => setPlaying(true)} className="portfolio-video-preview group" aria-label={`${title} — play video`}>
        <iframe
          src={`https://player.vimeo.com/video/${vm}?background=1&autoplay=1&muted=1&loop=1&title=0&byline=0&portrait=0&dnt=1`}
          title={`${title} preview`}
          className="portfolio-vimeo-preview"
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture"
          tabIndex="-1"
          aria-hidden="true"
        />
        <span className="portfolio-media-overlay" />
        <PlayBadge />
        <span className="portfolio-media-caption">{title}</span>
      </button>
    );
  }

  return null;
}
